import {getFirstVisibleElementFromSelector, IColumn, SelectionMode, Stack, Toggle} from "@fluentui/react";
import {RootState} from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import * as IXrmTypes from 'model/CRM';
import * as FAM from 'model/FlexibleAttributeModel';
import {EBenefitStatus, IDateValue, IFlexibleFieldSet, INonStandard} from 'model/FlexibleAttributeModel';
import React, {useMemo, useState} from "react";
import {connect, ConnectedProps, useDispatch} from "react-redux";
import {changeMergeSnap} from "../../global/Reducer/compSlice";
import  {createNonStandardColumns} from "../../global/Application";
import {StickyDetailsList} from "../../components/layout/StickyDetailList";
import NonStandardEditor from "../BlueRelationship/NonStandardEditor";
import {formatDate, parseDate} from "../../global/Utility/TSFunctions";
import {onRenderColumnRef} from "../../components/controls/Grid";

interface IGridItems<T> {
    [key: string]: string | number | any;
    _index: number;
    key: string;
    data?: T;
}

type TFlexibleFieldGridItems = IGridItems<FAM.IFlexibleFieldSet> | IGridItems<any>;
interface IProps extends TComponentConnectedProps {
    level?: any;
    currentBenefit?: any;
    section?: any;
    label?: string;
    configAttribute?: IXrmTypes.bcbs_config_attribute;
    masterAttribute?: IXrmTypes.bcbs_config_masterattribute;
    category: FAM.IBenefit | IXrmTypes.bcbs_config_category;
    scope?: FAM.IGroupConfigurationData | FAM.IPlan | FAM.IBenefit | FAM.IFeesAndPremiums;
    disabled?: boolean;
    snap?: FAM.IGroupConfigurationData
    snap2?: FAM.IGroupConfigurationData
    mergeSnap?: FAM.IGroupConfigurationData
    merge?: boolean | undefined
    compare?: boolean | undefined
    comparison?: boolean | undefined
    side?: 'left' | 'right' | 'merge' | undefined
    leftright: boolean | undefined
    counter: number;
    noDiff?: () => void | undefined;
    tierNames: Record<string, FAM.ITierLevel> | null
    id?: string;
}

const AttributeSetGridView: React.FC<IProps> = (props) => {

    const dispatch = useDispatch();
    const [counter, setCounter] = useState<number>(0);
    const [leftRight, setLeftRight] = useState<Record<string, boolean>>({"default": props.leftright!})

    const [nonStandard, setNonStandard] = useState<FAM.INonStandard | null>(null);

    React.useEffect(() => {
        setLeftRight({"default": props.leftright!});
        setCounter(props.counter + 1);
    }, [props.leftright, props.counter]);
    const categoryMapByName = props.state.data.config.cache.map_bcbs_config_category_named;
    const columns = useMemo(() => {
        const ret: IColumn[] = [];
        const attributeSets = props.state.data.config.cache.map_bcbs_config_attribute_sets[props.masterAttribute?.bcbs_config_masterattributeid!];
        if (props.category === categoryMapByName["Group Number"].bcbs_config_categoryid) {
            ret.push({
                key: 'cesNumber',
                name: 'CES Client Number',
                fieldName: 'cesNumber',
                minWidth: 85,
            });
            ret.push({
                key: 'groupNumber',
                name: 'Group Number',
                fieldName: 'groupNumber',
                minWidth: 120,
            });
            ret.push({
                key: 'planRef',
                name: 'Plan',
                fieldName: 'planRef',
                minWidth: 120,
            });

            ret.push({
                key: 'accountsReceivableRef',
                name: 'Accounts Receivable',
                fieldName: 'accountsReceivableRef',
                minWidth: 120,

            });

            ret.push({
                key: 'stopLossRef',
                name: 'Stop Loss',
                fieldName: 'stopLossRef',
                minWidth: 120,
            });

            props.state.data.config.tabs.forEach(eachTab => {
                // only tabs that are associated with current config;
                if (eachTab._bcbs_config_categoryid_value !== categoryMapByName["Group Number"].bcbs_config_categoryid)
                    return;

                props.state.data.config.sections.map(eachSection => {
                    // lets skip sections that do not belong to my tab
                    if (eachSection._bcbs_config_tabid_value !== eachTab.bcbs_config_tabid)
                        return;

                    props.state.data.config?.attributes.forEach(eachAttribute => {
                        // let skip attributes that do not belong to my section
                        if (eachAttribute._bcbs_config_sectionid_value !== eachSection.bcbs_config_sectionid)
                            return;

                        const eachAttributeMaster = props.state.data.config.cache.map_bcbs_config_masterattribute[eachAttribute._bcbs_config_masterattributeid_value!];

                        const column: IColumn = {
                            key: eachAttribute.bcbs_config_attributeid!,
                            name: eachAttribute.bcbs_name!,
                            minWidth: eachAttribute.bcbs_viewcolumnwidth ?? (eachAttribute?.bcbs_name?.length ?? 10) * 8,
                            fieldName: `${eachAttribute.bcbs_config_attributeid}-`,
                            isResizable: true,
                            data: eachAttribute,
                        };

                        ret.push(column);
                    });
                });
            })
            ret.push({
                key: 'status',
                name: 'Status',
                fieldName: 'status',
                minWidth: 120,
            });
        }

        if (props.category.bcbs_config_categoryid === props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard'].bcbs_config_categoryid) {
            const columns = createNonStandardColumns(
                props.state.data.config,
                {
                    section: props.section,
                    benefit: props.currentBenefit,
                    tierNames: props.tierNames
                }
            );
            columns.push({key: 'referenceOnly',
                name: 'BCBS Reference Only',
                minWidth: 50,
                fieldName: `referenceOnly`,
                maxWidth: 150,
                isResizable: true});
            return columns.map((c) => {
                delete c.onRender;
                return c;
            });
        } else {


            attributeSets?.forEach(eachAttribute => {
                ret.push({
                    key: eachAttribute.bcbs_config_attributeid!,
                    name: eachAttribute.bcbs_name!,
                    minWidth: (eachAttribute?.bcbs_name?.length ?? 10) * 8,
                    fieldName: `${eachAttribute.bcbs_config_attributeid}-`,
                    maxWidth: 150,
                    isResizable: true
                });
            });
        }

        return ret;

    }, [props.state.data.config.cache.map_bcbs_config_attribute_sets, props.side, props.masterAttribute?.bcbs_config_masterattributeid]);


    const gridStyles = {
        root: {
            overflowX: 'scroll',
            marginBottom: '1.45em',
            selectors: {
                '& [role=grid]': {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    height: '60vh',
                },
            },
        },
        headerWrapper: {
            flex: '0 0 auto',
            selectors: {
                '.ms-DetailsHeader': {
                    paddingTop: '0px',
                },
            }
        },
        contentWrapper: {
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
        },
    };

    const category = props.category;
    const scope = props.scope;


    let target = [] as FAM.IFlexibleFieldSet[] | FAM.IGroupNumber[] | FAM.INonStandard[];
    let target2 = [] as FAM.IFlexibleFieldSet[] | FAM.IGroupNumber[] | FAM.INonStandard[];
    let mergeTarget = [] as FAM.IFlexibleFieldSet[] | FAM.IGroupNumber[] | FAM.INonStandard[];
    let flexibleFieldSets = [] as FAM.IFlexibleFieldSet[] | FAM.IGroupNumber[] | FAM.INonStandard[];
    let flexibleFieldSets2 = [] as FAM.IFlexibleFieldSet[] | FAM.IGroupNumber[] | FAM.INonStandard[];
    let flexibleFieldSetsMerge = [] as FAM.IFlexibleFieldSet[] | FAM.IGroupNumber[] | FAM.INonStandard[];
    let isGroupNumber = false;
    let isFFS = false;
    let isNonStandard = false;

    const snap: FAM.IGroupConfigurationData = props.snap || props.state.data.json;
    const targetBenefit = snap?.benefits?.filter(a => !a.deactivated).find(p => p.id === props.currentBenefit?.id) ?? snap?.benefits?.filter(a => !a.deactivated).find(p => p.name === props.currentBenefit?.name)
    const targetBenefit2 = props.snap2?.benefits?.filter(a => !a.deactivated).find(p => p.id === props.currentBenefit?.id) ?? props.snap2?.benefits?.filter(a => !a.deactivated).find(p => p.name === props.currentBenefit?.name)

    const highlightStyles = {backgroundColor: 'yellow'};
    if (category.bcbs_config_categoryid === categoryMapByName['Group Configuration'].bcbs_config_categoryid) {
        // Group Configuration
        target = snap?.attributeSets.filter(a => !a.deactivated) ?? []

        target2 = props.snap2?.attributeSets.filter(a => !a.deactivated) ?? []

        mergeTarget = props.mergeSnap?.attributeSets.filter(a => !a.deactivated) ?? []
        flexibleFieldSets = target.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSets2 = target2.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSetsMerge = mergeTarget.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        isFFS = true;

    } else if (category.bcbs_config_categoryid === categoryMapByName['Plan'].bcbs_config_categoryid) {
        // Plan
        target = snap?.plans?.find(q => q.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];

        target2 = props.snap2?.plans?.find(q => q.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];

        mergeTarget = props.mergeSnap?.plans?.find(q => q.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        flexibleFieldSets = target.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSets2 = target2.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSetsMerge = mergeTarget.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        isFFS = true;

    } else if (category.bcbs_config_categoryid === categoryMapByName["Fees and Premiums"].bcbs_config_categoryid) {
        // Fees And Premiums
        target = snap?.feesAndPremiums?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];

        target2 = props.snap2?.feesAndPremiums?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];

        mergeTarget = props.mergeSnap?.feesAndPremiums?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        flexibleFieldSets = target.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSets2 = target2.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSetsMerge = mergeTarget.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        isFFS = true;


    } else if (category === categoryMapByName["Group Number"].bcbs_config_categoryid) {
        // @ts-ignore
        if (props.scope?.benefitRefs) {
            target = snap?.groupStructure?.groupNumbers.filter(g => g.planRef?.id === props.scope?.id).filter(e => !(e.status?.value === EBenefitStatus["In-Progress"] && e.deactivated)) ?? [];
            target2 = props.snap2?.groupStructure?.groupNumbers.filter(g => g.planRef?.id === props.scope?.id).filter(e => !(e.status?.value === EBenefitStatus["In-Progress"] && e.deactivated)) ?? [];
            mergeTarget = props.mergeSnap?.groupStructure?.groupNumbers.filter(g => g.planRef?.id === props.scope?.id).filter(e => !(e.status?.value === EBenefitStatus["In-Progress"] && e.deactivated)) ?? [];
        } else {
            target = snap?.groupStructure?.groupNumbers.filter(e => !(e.status?.value === EBenefitStatus["In-Progress"] && e.deactivated)) ?? [];
            target2 = props.snap2?.groupStructure?.groupNumbers.filter(e => !(e.status?.value === EBenefitStatus["In-Progress"] && e.deactivated)) ?? [];
            mergeTarget = props.mergeSnap?.groupStructure?.groupNumbers.filter(e => !(e.status?.value === EBenefitStatus["In-Progress"] && e.deactivated)) ?? [];
        }
        flexibleFieldSets = target;
        flexibleFieldSets2 = target2;
        flexibleFieldSetsMerge = mergeTarget;
        isGroupNumber = true;

    } else if (category.bcbs_config_categoryid === categoryMapByName["Stop Loss"].bcbs_config_categoryid) {
        // stop loss
        target = snap?.stopLoss?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        target2 = props.snap2?.stopLoss?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        mergeTarget = props.mergeSnap?.stopLoss?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        flexibleFieldSets = target.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSets2 = target2.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSetsMerge = mergeTarget.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        isFFS = true;

    } else if (category.bcbs_config_categoryid === categoryMapByName["Accounts Receivable"].bcbs_config_categoryid) {
        //claims AR
        target = snap?.claimsARs?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        target2 = props.snap2?.claimsARs?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        mergeTarget = props.mergeSnap?.claimsARs?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        flexibleFieldSets = target.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSets2 = target2.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSetsMerge = mergeTarget.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        isFFS = true;

    } else if (props.state.data.config.cache.bcbs_config_category_benefits.some(q => q.bcbs_config_categoryid === category.bcbs_config_categoryid)) {
        target = targetBenefit?.attributeSets.filter(a => !a.deactivated) ?? [];
        target2 = targetBenefit2?.attributeSets.filter(a => !a.deactivated) ?? [];
        mergeTarget = props.mergeSnap?.benefits?.find(p => p.id === scope?.id)?.attributeSets.filter(a => !a.deactivated) ?? [];
        flexibleFieldSets = target.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSets2 = target2.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        flexibleFieldSetsMerge = mergeTarget.filter(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid);
        isFFS = true;

    } else if (category.bcbs_config_categoryid === props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard'].bcbs_config_categoryid) {
        if (props.level === "groupConfiguration") {
            target = snap?.nonStandards?.filter(a => !a.deactivated) ?? [];
            target2 = props.snap2?.nonStandards?.filter(a => !a.deactivated) ?? [];
            mergeTarget = props.mergeSnap?.nonStandards?.filter(a => !a.deactivated) ?? [];

        } else if (props.level === "section") {
            target = targetBenefit?.nonStandards.filter(a => !a.deactivated).filter(n => n.sectionRef?.id === props.section?.bcbs_config_sectionid) ?? [];
            target2 = targetBenefit2?.nonStandards.filter(a => !a.deactivated).filter(n => n.sectionRef?.id === props.section?.bcbs_config_sectionid) ?? [];
            mergeTarget = props.mergeSnap?.benefits?.filter(a => !a.deactivated).find(p => p.id === props.currentBenefit.id)?.nonStandards.filter(a => !a.deactivated).filter(n => n.sectionRef?.id === props.section?.bcbs_config_sectionid) ?? [];
        }
        flexibleFieldSets = target;
        flexibleFieldSets2 = target2;
        flexibleFieldSetsMerge = mergeTarget;
        isNonStandard = true;
    }

    let items: TFlexibleFieldGridItems[] = [];
    let items2: TFlexibleFieldGridItems[] = [];
    let itemsMerge: TFlexibleFieldGridItems[] = [];
    const _itemsInCommon: TFlexibleFieldGridItems[] = [];
    const _itemsInCommon2: TFlexibleFieldGridItems[] = [];
    const _itemsInCommonMerge: TFlexibleFieldGridItems[] = [];
    const _itemsLeft: TFlexibleFieldGridItems[] = [];
    const _itemsRight: TFlexibleFieldGridItems[] = [];
    const _itemsInMerge: TFlexibleFieldGridItems[] = [];
    flexibleFieldSets?.forEach((eFieldSet: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard, ix1: number) => {
        //eFieldSet IS one LINE of the grid

        let compareFS: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard | undefined = (flexibleFieldSets2 as any).find((n: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard) => n.id === eFieldSet.id);
        let mergeFS: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard | undefined = (flexibleFieldSetsMerge as any).find((n: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard) => n.id === eFieldSet.id);
        // so are compareFS and mergeFS

        if (isFFS) {
            if ((flexibleFieldSets2 as IFlexibleFieldSet[])?.filter((n: FAM.IFlexibleFieldSet) => n.id === eFieldSet.id)?.length > 1) {
                let compareFSTemp = (flexibleFieldSets2 as IFlexibleFieldSet[]).filter((n: FAM.IFlexibleFieldSet) => n.id === eFieldSet.id);
                if ((compareFSTemp as IFlexibleFieldSet[])?.length > 1)
                    compareFS = (compareFSTemp as IFlexibleFieldSet[])?.find((n: FAM.IFlexibleFieldSet) => n.name === (eFieldSet as FAM.IFlexibleFieldSet).name);
                else if ((compareFSTemp as IFlexibleFieldSet[])?.length === 1)
                    compareFS = compareFSTemp[0];
                else if ((compareFSTemp as IFlexibleFieldSet[])?.length < 1)
                    compareFS = (flexibleFieldSets2 as IFlexibleFieldSet[])?.find((n: FAM.IFlexibleFieldSet) => n.name === (eFieldSet as FAM.IFlexibleFieldSet).name);

            }
            if ((flexibleFieldSetsMerge as IFlexibleFieldSet[])?.filter((n: FAM.IFlexibleFieldSet) => n.id === eFieldSet.id)?.length > 1) {
                mergeFS = (flexibleFieldSetsMerge as IFlexibleFieldSet[]).find((n: FAM.IFlexibleFieldSet) => n.id === eFieldSet.id && n.name === (eFieldSet as FAM.IFlexibleFieldSet).name);
            }

        }
        const keyID: string = `${isGroupNumber ? (eFieldSet as FAM.IGroupNumber).groupNumber : eFieldSet.id}` ?? eFieldSet.id;
        if (isGroupNumber) {
            compareFS = (flexibleFieldSets2 as FAM.IGroupNumber[]).find((n: FAM.IGroupNumber) => n.groupNumber === (eFieldSet as FAM.IGroupNumber).groupNumber);
            mergeFS = (flexibleFieldSetsMerge as FAM.IGroupNumber[]).find((n: FAM.IGroupNumber) => n.groupNumber === (eFieldSet as FAM.IGroupNumber).groupNumber);
        }
        const _items: TFlexibleFieldGridItems = {
            _index: ix1,
            key: keyID!,
            data: eFieldSet
        };
        if (isGroupNumber) {
            _items["cesNumber"] = compareFS ? (eFieldSet as FAM.IGroupNumber)?.cesNumberRef?.name :
                <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.cesNumberRef?.name}</span>;
            _items["groupNumber"] = compareFS ? `${(eFieldSet as FAM.IGroupNumber)?.groupNumber} ${(eFieldSet as FAM.IGroupNumber)?.status?.value === EBenefitStatus.Cancelled ? '(Cancelled)' : ''}` :
                <span
                    style={highlightStyles}>{`${(eFieldSet as FAM.IGroupNumber)?.groupNumber} ${(eFieldSet as FAM.IGroupNumber)?.status?.value === EBenefitStatus.Cancelled ? '(Cancelled)' : ''}`}</span>;
            _items["planRef"] = compareFS ? (eFieldSet as FAM.IGroupNumber)?.planRef?.name :
                <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.planRef?.name}</span>;
            _items["stopLossRef"] =  compareFS ? (eFieldSet as FAM.IGroupNumber)?.stopLossRef?.name :
                <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.stopLossRef?.name}</span>;
            _items["accountsReceivableRef"] =  compareFS ? (eFieldSet as FAM.IGroupNumber)?.accountsReceivableRef?.name :
                <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.accountsReceivableRef?.name}</span>;
            _items["status"] = compareFS ? (eFieldSet as FAM.IGroupNumber)?.status?.name :
                <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.status?.name}</span>;


        }
        if (isNonStandard) {
            _items["name"] = compareFS ? (eFieldSet as FAM.INonStandard)?.name :
                <span style={highlightStyles}>{(eFieldSet as FAM.INonStandard)?.name}</span>;

            _items["referenceOnly"] = compareFS ? (eFieldSet as FAM.INonStandard)?.referenceOnly ? 'Yes' : 'No' :
                <span style={highlightStyles}>{(eFieldSet as FAM.INonStandard)?.referenceOnly ? 'Yes' : 'No'}</span>;
        }

        eFieldSet.fields.forEach(eachField => {
            for (var i = 0; i < eachField.values.length; i++) {
                const otherField = compareFS?.fields.find(f => f.configRef.id === eachField.configRef.id)?.values[i];

                const field = eachField.values[i];
                const fieldName = `${eachField.configRef.id}-${eachField.values[i]?.tierRef?.id ?? ''}`
                if (field.value?.type === "string") {
                    //@ts-ignore
                    _items[fieldName] = (otherField && otherField?.value?.value !== field?.value?.value) || otherField === undefined ?
                        <span style={highlightStyles}>{field.value.value}</span> : <span>{field.value.value}</span>;
                } else if (field.value?.type === "bool") {
                    //@ts-ignore
                    _items[fieldName] = (otherField && otherField?.value?.value !== field?.value?.value) || otherField === undefined ?
                        <span style={highlightStyles}>{field.value.value ? "Yes" : "No"}</span> :
                        <span>{field.value.value ? "Yes" : "No"}</span>;
                } else if (field.value?.type === "date") {
                    //@ts-ignore
                    _items[fieldName] = (otherField && otherField?.value?.value !== field?.value?.value) || otherField === undefined ?
                        <span
                            style={highlightStyles}>{formatDate(parseDate(field.value.value, 'local:yyyy-MM-dd'), 'en-US-ET')}</span> :
                        <span>{formatDate(parseDate(field.value.value, 'local:yyyy-MM-dd'), 'en-US-ET')}</span>;
                } else if (field.value?.type === "optionset") {
                    //@ts-ignore
                    _items[fieldName] = (otherField && otherField?.value?.name !== field?.value?.name) || otherField === undefined ?
                        <span style={highlightStyles}>{field.value.name}</span> : <span>{field.value.name}</span>;
                } else if (field.value?.type === "entity") {
                    //@ts-ignore
                    _items[fieldName] = (otherField && otherField?.value?.name !== field?.value?.name) || otherField === undefined ?
                        <span style={highlightStyles}>{field.value.name}</span> : <span>{field.value.name}</span>;
                } else if (field.value?.type === "number") {
                    //@ts-ignore
                    _items[fieldName] = (otherField && otherField?.value?.value !== field?.value?.value) || otherField === undefined ?
                        <span style={highlightStyles}>{field.value.value}</span> : <span>{field.value.value}</span>;
                }
            }
        });
        const blank: TFlexibleFieldGridItems = {
            key: "blank" + ix1,
            _index: ix1,
        }
        const _itemsMerge: TFlexibleFieldGridItems = {
            _index: ix1,
            key: keyID!,
            data: mergeFS
        };

        if (isGroupNumber) {
            _itemsMerge["cesNumber"] = !mergeFS?.deactivated && (mergeFS as FAM.IGroupNumber)?.cesNumberRef?.name;
            _itemsMerge["groupNumber"] = !mergeFS?.deactivated && `${(mergeFS as FAM.IGroupNumber)?.groupNumber} ${(mergeFS as FAM.IGroupNumber)?.status?.value === EBenefitStatus.Cancelled ? '(Cancelled)' : ''}`;
            _itemsMerge["planRef"] = !mergeFS?.deactivated && (mergeFS as FAM.IGroupNumber)?.planRef?.name;
            _itemsMerge["stopLossRef"] =  !mergeFS?.deactivated && (mergeFS as FAM.IGroupNumber)?.stopLossRef?.name;
            _itemsMerge["accountsReceivableRef"] =  !mergeFS?.deactivated && (mergeFS as FAM.IGroupNumber)?.accountsReceivableRef?.name;
            _itemsMerge["status"] = !mergeFS?.deactivated && (mergeFS as FAM.IGroupNumber)?.status?.name;
        }
        if (isNonStandard) {
            _itemsMerge["name"] = !mergeFS?.deactivated && (eFieldSet as FAM.INonStandard)?.name;
            _itemsMerge["referenceOnly"] = !mergeFS?.deactivated && (eFieldSet as FAM.INonStandard)?.referenceOnly ? 'Yes' : 'No';
        }
        const bo = JSON.stringify(eFieldSet) === JSON.stringify(compareFS);
        _itemsMerge['merge'] = bo ? <span>No Changes</span> :
            <Toggle onText="Right" offText="Left"
                    key={keyID}

                    onChange={(event, checked) => {
                        setCounter(counter + 1);
                        setLeftRight((prevState) => {
                            if (prevState[eFieldSet.id] !== undefined && prevState[eFieldSet.id] !== null)
                                return {
                                    ...prevState,
                                    [eFieldSet.id]: !prevState[eFieldSet.id]
                                }
                            return {...prevState, [eFieldSet.id]: !props.leftright}
                        })
                        // For each category attribute set, or nonstandard, we need to appropriately either:
                        // 1) Slice the existing array and replace an item in the array
                        // 2) Slice the existing array to remove an item
                        // 3) Add an item to the existing array

                        // This is the toggle for the first snapshot item where a match may or may not have existed.
                        // Currently, "first snapshot" === left snapshot. If this changes, this logic WILL BREAK because of the
                        // usage of checked to know which side we are looking at
                        // Case 3 can still happen if Case 2 is triggered and then undone

                        if (category.bcbs_config_categoryid === categoryMapByName['Group Configuration'].bcbs_config_categoryid) {
                            const fieldIndex = props.state.comp.mergeSnap.json?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                            if (checked) {
                                // toggle was switched to "Right"
                                if (!compareFS) {
                                    // item does not exist on Right snapshot
                                    // Remove from the array because it does not exist now
                                    if (fieldIndex > -1)
                                        // Don't try to remove if it's not there
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                attributeSets: [...props.state.comp.mergeSnap.json?.attributeSets?.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.attributeSets?.slice(fieldIndex + 1)]
                                            }
                                        }));
                                } else {
                                    // Item does exist on Right Snapshot
                                    // Add to the array
                                    if (fieldIndex > -1) {
                                        // Only slice if it already exists in the array
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                benefits: [...props.state.comp.mergeSnap.json?.attributeSets?.slice(0, fieldIndex), compareFS, ...props.state.comp.mergeSnap.json?.benefits?.slice(fieldIndex + 1)]
                                            }
                                        }));
                                    } else {
                                        // Otherwise append to the array
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                benefits: [...props.state.comp.mergeSnap.json?.attributeSets, compareFS]
                                            }
                                        }));
                                    }
                                }
                            } else {
                                // Toggle was switched to "Left"
                                if (!compareFS) {
                                    // add to array because it does not exist
                                    dispatch(changeMergeSnap({
                                        ...props.state.comp.mergeSnap, json: {
                                            ...props.state.comp.mergeSnap.json,
                                            attributeSets: [...props.state.comp.mergeSnap.json?.attributeSets, eFieldSet]
                                        }
                                    }));
                                } else {
                                    // We shouldn't have to check that the index exists because it should have matched with !compareFS
                                    // But just to eliminate the possibility
                                    if (fieldIndex > -1)
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                attributeSets: [...props.state.comp.mergeSnap.json?.attributeSets?.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.attributeSets?.slice(fieldIndex + 1)]
                                            }
                                        }));
                                    else
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                attributeSets: [...props.state.comp.mergeSnap.json?.attributeSets, eFieldSet]
                                            }
                                        }));
                                }
                            }
                        } else if (isNonStandard) {
                            if (props.level === "groupConfiguration") {
                                const fieldIndex = props.state.comp.mergeSnap.json?.nonStandards?.findIndex(q => q.id === eFieldSet.id) ?? -1;
                                if (checked) {
                                    // Toggle was switched to "Right"
                                    if (compareFS) {
                                        // item does exist on Right Snapshot
                                        if (fieldIndex > -1) {
                                            // If exists in array, replace it
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards?.slice(0, fieldIndex), compareFS, ...props.state.comp.mergeSnap.json?.nonStandards?.slice(fieldIndex + 1)]
                                                }
                                            }));
                                        } else {
                                            // Didn't exist on snapshot, add it
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards, compareFS]
                                                }
                                            }));
                                        }
                                    } else {
                                        // Doesn't exist on Right Snapshot, remove it
                                        if (fieldIndex > -1) {
                                            // Only remove if it exists
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards?.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.nonStandards?.slice(fieldIndex + 1)]
                                                }
                                            }));
                                        }
                                    }
                                } else {
                                    // Toggle was switched to "left"
                                    if (fieldIndex !== -1) {
                                        // If it exists in the array, swap it
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards?.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.nonStandards?.slice(fieldIndex + 1)]
                                            }
                                        }));
                                    } else {
                                        // Otherwise, add it
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards, eFieldSet]
                                            }
                                        }));
                                    }
                                }
                            } else if (props.level === "section") {
                                const benefit = props.state.comp.mergeSnap.json?.benefits?.find(p => p.id === props.currentBenefit.id) ?? props.state.comp.mergeSnap.json?.benefits?.find(p => p.name === props.currentBenefit.name);
                                let benefitIndex = props.state.comp.mergeSnap.json?.benefits?.findIndex(p => p.id === benefit?.id);
                                const fieldIndex = benefit?.nonStandards?.findIndex(n => n.id === eFieldSet.id) ?? -1;

                                if (checked) {
                                    // Toggle was switched to "Right"
                                    if (compareFS) {
                                        // Item does exist on Right
                                        // @ts-ignore
                                        const newBenefit = {
                                            ...benefit,
                                            nonStandards: [...benefit!.nonStandards.slice(0, fieldIndex), compareFS, ...benefit!.nonStandards.slice(fieldIndex + 1)]
                                        }
                                        // We aren't going to check on benefit index, because you shouldn't have toggles rendered if the benefit doesn't match on both sides

                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                benefits: [...props.state.comp.mergeSnap.json?.benefits?.slice(0, benefitIndex), newBenefit, ...props.state.comp.mergeSnap.json?.benefits?.slice(benefitIndex + 1)]
                                            }
                                        }));
                                    } else {
                                        //Item does not exist on right, let's remove it
                                        // @ts-ignore
                                        if (fieldIndex > -1) {
                                            // Verify it's really in the array first
                                            const newBenefit = {
                                                ...benefit,
                                                nonStandards: [...benefit!.nonStandards.slice(0, fieldIndex), ...benefit!.nonStandards.slice(fieldIndex + 1)]
                                            }
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    benefits: [...props.state.comp.mergeSnap.json?.benefits?.slice(0, benefitIndex), newBenefit, ...props.state.comp.mergeSnap.json?.benefits?.slice(benefitIndex + 1)]
                                                }
                                            }));
                                        }
                                    }
                                } else {
                                    // Toggle was switched to "Left"
                                    if (fieldIndex !== -1) {
                                        // Item is in array
                                        const newBenefit = {
                                            ...benefit,
                                            nonStandards: [...benefit!.nonStandards.slice(0, fieldIndex), eFieldSet, ...benefit!.nonStandards.slice(fieldIndex + 1)]
                                        }
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                benefits: [...props.state.comp.mergeSnap.json?.benefits?.slice(0, benefitIndex), newBenefit, ...props.state.comp.mergeSnap.json?.benefits?.slice(benefitIndex + 1)]
                                            }
                                        }));

                                    } else {
                                        // add to array
                                        const newBenefit = {
                                            ...benefit,
                                            nonStandards: [...benefit!.nonStandards, eFieldSet]
                                        }
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                benefits: [...props.state.comp.mergeSnap.json?.benefits?.slice(0, benefitIndex), newBenefit, ...props.state.comp.mergeSnap.json?.benefits?.slice(benefitIndex + 1)]
                                            }
                                        }));
                                    }

                                }
                            }
                        } else if (category === categoryMapByName["Group Number"].bcbs_config_categoryid) {
                            console.log(props.state.comp.mergeSnap.json?.groupStructure);
                            const fieldIndex = props.state.comp.mergeSnap.json?.groupStructure.groupNumbers.findIndex(q => q.groupNumber === (eFieldSet as FAM.IGroupNumber).groupNumber) ?? -1;
                            if (checked) {
                                // Toggle switched to the Right
                                if (!compareFS) {
                                    // Item doesn't exist on Right
                                    if (fieldIndex > -1) {
                                        // Make sure it exists before deleting
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                groupStructure: {
                                                    ...props.state.comp.mergeSnap.json.groupStructure,
                                                    groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)]
                                                }
                                            }
                                        }));
                                    }
                                } else {
                                    // Does exist on Right
                                    const cesNumber = props.state.comp.rightSnap.json?.groupStructure?.cesNumbers?.find(q => q?.cesNumber === (eFieldSet as FAM.IGroupNumber).cesNumberRef?.name)
                                    const cesIndex = props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.findIndex(q => q?.cesNumber === cesNumber?.cesNumber);
                                    if (cesNumber) {
                                        if (fieldIndex > -1) {
                                            if (cesIndex > -1)
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), compareFS, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(0, cesIndex), cesNumber, ...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(cesIndex + 1)]
                                                        }
                                                    }
                                                }));
                                            else
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), compareFS, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers, cesNumber]
                                                        }
                                                    }
                                                }));
                                        } else {
                                            if (cesIndex > -1)
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, compareFS],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(0, cesIndex), cesNumber, ...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(cesIndex + 1)]
                                                        }
                                                    }
                                                }));
                                            else
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, compareFS],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers, cesNumber]
                                                        }
                                                    }
                                                }));
                                        }
                                    } else {
                                        if (fieldIndex > -1)
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    groupStructure: {
                                                        ...props.state.comp.mergeSnap.json.groupStructure,
                                                        groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), compareFS, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)],
                                                    }
                                                }
                                            }));
                                        else
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    groupStructure: {
                                                        ...props.state.comp.mergeSnap.json.groupStructure,
                                                        groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, compareFS],
                                                    }
                                                }
                                            }));

                                    }
                                }
                            } else {
                                // Toggle switched to the Left
                                let newItem;
                                const cesNumber = props.state.comp.leftSnap.json?.groupStructure?.cesNumbers?.find(q => q?.cesNumber === (eFieldSet as FAM.IGroupNumber).cesNumberRef?.name)
                                const cesIndex = props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.findIndex(q => q?.cesNumber === cesNumber?.cesNumber);
                                if (!compareFS) {
                                    // Doesn't exist on Right
                                    if (cesNumber) {
                                        if (cesIndex > -1)
                                            newItem = {
                                                ...props.state.comp.mergeSnap.json?.groupStructure,
                                                groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                                cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(0, cesIndex), cesNumber, ...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(cesIndex + 1)]
                                            };
                                        else
                                            newItem = {
                                                ...props.state.comp.mergeSnap.json?.groupStructure,
                                                groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                                cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers, cesNumber]
                                            };
                                    } else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.groupStructure,
                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                        };
                                } else {
                                    // Does exist on right
                                    if (fieldIndex > -1) {
                                        if (cesNumber) {
                                            if (cesIndex > -1)
                                                newItem = {
                                                    ...props.state.comp.mergeSnap.json?.groupStructure,
                                                    groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers.slice(fieldIndex + 1)],
                                                    cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(0, cesIndex), cesNumber, ...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(cesIndex + 1)]

                                                };
                                            else
                                                newItem = {
                                                    ...props.state.comp.mergeSnap.json?.groupStructure,
                                                    groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers.slice(fieldIndex + 1)],
                                                    cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers, cesNumber]
                                                };
                                        } else newItem = {
                                            ...props.state.comp.mergeSnap.json?.groupStructure,
                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers.slice(fieldIndex + 1)],
                                        };
                                    } else {
                                        if (cesNumber) {
                                            if (cesIndex > -1)
                                                newItem = {
                                                    ...props.state.comp.mergeSnap.json?.groupStructure,
                                                    groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                                    cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(0, cesIndex), cesNumber, ...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(cesIndex + 1)]

                                                };
                                            else
                                                newItem = {
                                                    ...props.state.comp.mergeSnap.json?.groupStructure,
                                                    groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                                    cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers, cesNumber]
                                                };
                                        } else newItem = {
                                            ...props.state.comp.mergeSnap.json?.groupStructure,
                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                        };
                                    }
                                }
                                dispatch(changeMergeSnap({
                                    ...props.state.comp.mergeSnap, json: {
                                        ...props.state.comp.mergeSnap.json,
                                        groupStructure: newItem
                                    }
                                }));

                            }

                        } else if (category.bcbs_config_categoryid === categoryMapByName['Plan'].bcbs_config_categoryid) {
                            let indexer = props.state.comp.mergeSnap.json?.plans?.findIndex(q => q.id === scope?.id);
                            if (indexer === -1)
                                indexer = props.state.comp.mergeSnap.json?.plans?.findIndex(q => q.name === scope?.name);
                            const fieldIndex = props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                            let newItem = props.state.comp.mergeSnap.json?.plans[indexer];
                            // Indexer must exist because toggles would not show if there was not a plan on both sides
                            if (checked) {
                                // Toggled to right
                                if (!compareFS) {
                                    // Doesn't exist on Right, remove it
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.plans[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                } else {
                                    // Swap if it exists
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.plans[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(0, fieldIndex), (compareFS as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else {
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.plans[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets, (compareFS as IFlexibleFieldSet)]
                                        };
                                    }
                                }
                            } else {
                                //Toggle Left
                                if (!compareFS || fieldIndex === -1) {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.plans[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.plans[indexer].attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                    };
                                } else {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.plans[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                    };
                                }
                            }
                            dispatch(changeMergeSnap({
                                ...props.state.comp.mergeSnap, json: {
                                    ...props.state.comp.mergeSnap.json,
                                    plans: [...props.state.comp.mergeSnap.json?.plans?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.plans?.slice(indexer + 1)]
                                }
                            }));

                        } else if (category.bcbs_config_categoryid === categoryMapByName["Fees and Premiums"].bcbs_config_categoryid) {
                            // Fees And Premiums
                            const indexer = props.state.comp.mergeSnap.json?.feesAndPremiums?.findIndex(q => q.id === scope?.id);
                            const fieldIndex = props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                            let newItem = props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer];
                            if (checked) {
                                //Toggled to the right
                                if (!compareFS) {
                                    //Not on Right snapshot, remove
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.feesAndPremiums[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };

                                } else {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.feesAndPremiums[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(0, fieldIndex), (compareFS as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.feesAndPremiums[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets, (compareFS as IFlexibleFieldSet)]
                                        };
                                }
                            } else {
                                // Toggle to left
                                if (!compareFS || fieldIndex === -1) {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.feesAndPremiums[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                    };
                                } else {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.feesAndPremiums[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                    };
                                }
                            }
                            dispatch(changeMergeSnap({
                                ...props.state.comp.mergeSnap, json: {
                                    ...props.state.comp.mergeSnap.json,
                                    feesAndPremiums: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.feesAndPremiums?.slice(indexer + 1)]
                                }
                            }));
                        } else if (category.bcbs_config_categoryid === categoryMapByName["Stop Loss"].bcbs_config_categoryid) {
                            // stop loss
                            const indexer = props.state.comp.mergeSnap.json?.stopLoss?.findIndex(q => q.id === scope?.id);
                            const fieldIndex = props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id);
                            let newItem = props.state.comp.mergeSnap.json?.stopLoss[indexer];
                            if (checked) {
                                // Toggle to the right
                                if (!compareFS) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.stopLoss[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };

                                } else {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.stopLoss[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(0, fieldIndex), (compareFS as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.stopLoss[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets, (compareFS as IFlexibleFieldSet)]
                                        };
                                }
                            } else {
                                if (!compareFS || fieldIndex === -1) {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.stopLoss[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                    };
                                } else {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.stopLoss[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                    };
                                }

                            }
                            dispatch(changeMergeSnap({
                                ...props.state.comp.mergeSnap, json: {
                                    ...props.state.comp.mergeSnap.json,
                                    stopLoss: [...props.state.comp.mergeSnap.json?.stopLoss?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.stopLoss?.slice(indexer + 1)]
                                }
                            }));
                        } else if (category.bcbs_config_categoryid === categoryMapByName["Accounts Receivable"].bcbs_config_categoryid) {
                            //claims AR
                            const indexer = props.state.comp.mergeSnap.json?.claimsARs?.findIndex(q => q.id === scope?.id);
                            const fieldIndex = props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id);
                            let newItem = props.state.comp.mergeSnap.json?.claimsARs[indexer];
                            if (checked) {
                                if (!compareFS) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.claimsARs[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                } else {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.claimsARs[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(0, fieldIndex), (compareFS as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                    };
                                }
                            } else {
                                if (!compareFS || fieldIndex === -1) {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.claimsARs[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                    };
                                } else {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.claimsARs[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                    };
                                }
                            }
                            dispatch(changeMergeSnap({
                                ...props.state.comp.mergeSnap, json: {
                                    ...props.state.comp.mergeSnap.json,
                                    claimsARs: [...props.state.comp.mergeSnap.json?.claimsARs?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.claimsARs?.slice(indexer + 1)]
                                }
                            }));


                        } else if (props.state.data.config.cache.bcbs_config_category_benefits.some(q => q.bcbs_config_categoryid === category.bcbs_config_categoryid)) {
                            const indexer = props.state.comp.mergeSnap.json?.benefits?.findIndex(q => q.id === scope?.id);
                            const fieldIndex = props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id);
                            let newItem = props.state.comp.mergeSnap.json?.benefits?.[indexer];
                            if (checked) {
                                if (!compareFS) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                } else {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(0, fieldIndex), (compareFS as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets, (compareFS as IFlexibleFieldSet)]
                                        };
                                }
                            } else {
                                if (!compareFS || fieldIndex === -1) {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                    };
                                } else {
                                    newItem = {
                                        ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                        attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                    };
                                }
                            }
                            dispatch(changeMergeSnap({
                                ...props.state.comp.mergeSnap, json: {
                                    ...props.state.comp.mergeSnap.json,
                                    benefits: [...props.state.comp.mergeSnap.json?.benefits?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.benefits?.slice(indexer + 1)]
                                }
                            }));
                        }
                    }}
                    checked={leftRight[eFieldSet.id] !== undefined ? leftRight[eFieldSet.id]! : props.leftright}
                    style={{}} onClick={(event) => {
                if (event?.stopPropagation) event.stopPropagation();
            }}

            />;

        let change = false;

        const _items2: TFlexibleFieldGridItems = {
            _index: ix1,
            key: keyID,
            data: compareFS
        };
        if (compareFS) {
            if (isGroupNumber) {

                _items2["cesNumber"] = (compareFS as FAM.IGroupNumber)?.cesNumberRef?.name;
                _items2["groupNumber"] = `${(compareFS as FAM.IGroupNumber)?.groupNumber} ${(compareFS as FAM.IGroupNumber)?.status?.value === EBenefitStatus.Cancelled ? '(Cancelled)' : ''}`;
                _items2["planRef"] = (compareFS as FAM.IGroupNumber)?.planRef?.name;
                _items2["stopLossRef"] =  (compareFS as FAM.IGroupNumber)?.stopLossRef?.name;
                _items2["accountsReceivableRef"] =  (compareFS as FAM.IGroupNumber)?.accountsReceivableRef?.name;
                _items2["status"] = (compareFS as FAM.IGroupNumber)?.status?.name;

            }
            if (isNonStandard) {
                _items2["name"] = (compareFS as FAM.INonStandard)?.name;
                _items2["referenceOnly"] = (compareFS as FAM.INonStandard)?.referenceOnly ? 'Yes' : 'No';
            }
            compareFS.fields.forEach(eachField => {
                for (var i = 0; i < eachField.values.length; i++) {
                    const otherField = eFieldSet?.fields.find(f => f.configRef.id === eachField.configRef.id)?.values[i] as any;

                    const field = eachField.values[i];
                    const fieldName = `${eachField.configRef.id}-${eachField.values[i]?.tierRef?.id ?? ''}`

                    if (otherField) {
                        if (field.value?.type === "string" || otherField.value?.type === "string") {
                            _items2[fieldName] = otherField && otherField?.value?.value !== (field?.value as FAM.IStringValue)?.value ?
                                <span style={highlightStyles}>{(field?.value as FAM.IStringValue)?.value}</span> :
                                <span>{(field?.value as FAM.IStringValue)?.value}</span>;
                            change = change || otherField?.value?.value !== (field?.value as FAM.IStringValue)?.value;
                        } else if (field.value?.type === "bool" || otherField.value?.type === "bool") {
                            _items2[fieldName] = otherField && otherField?.value?.value !== (field?.value as FAM.IBooleanValue)?.value ?
                                <span
                                    style={highlightStyles}>{(field?.value as FAM.IBooleanValue)?.value ? "Yes" : "No"}</span> :
                                <span>{(field?.value as FAM.IBooleanValue)?.value ? "Yes" : "No"}</span>;
                            change = change || otherField?.value?.value !== (field?.value as FAM.IBooleanValue)?.value;
                        } else if (field.value?.type === "date" || otherField.value?.type === "date") {
                            _items2[fieldName] = otherField && otherField?.value?.value !== (field?.value as FAM.IDateValue)?.value ?
                                <span
                                    style={highlightStyles}>{formatDate(parseDate((field?.value as IDateValue)?.value, 'local:yyyy-MM-dd'), 'en-US-ET')}</span> :
                                <span>{formatDate(parseDate((field?.value as IDateValue)?.value, 'local:yyyy-MM-dd'), 'en-US-ET')}</span>;
                            change = change || otherField?.value?.value !== (field?.value as FAM.IDateValue)?.value;
                        } else if (field.value?.type === "optionset" || otherField.value?.type === "optionset") {
                            _items2[fieldName] = otherField && otherField?.value?.name !== (field?.value as FAM.IOptionSetValue)?.name ?
                                <span style={highlightStyles}>{(field?.value as FAM.IOptionSetValue)?.name}</span> :
                                <span>{(field?.value as FAM.IOptionSetValue)?.name}</span>;
                            change = change || otherField?.value?.name !== (field?.value as FAM.IOptionSetValue)?.name;
                        } else if (field.value?.type === "entity" || otherField.value?.type === "entity") {
                            _items2[fieldName] = otherField && otherField?.value?.name !== (field?.value as FAM.IEntityReferenceValue)?.name ?
                                <span
                                    style={highlightStyles}>{(field?.value as FAM.IEntityReferenceValue)?.name}</span> :
                                <span>{(field?.value as FAM.IEntityReferenceValue)?.name}</span>;
                            change = change || otherField?.value?.name !== (field?.value as FAM.IEntityReferenceValue)?.name
                        } else if (field.value?.type === "number" || otherField.value?.type === "number") {
                            _items2[fieldName] = otherField && otherField?.value?.value !== (field?.value as FAM.INumberValue)?.value ?
                                <span style={highlightStyles}>{(field?.value as FAM.INumberValue)?.value}</span> :
                                <span>{(field?.value as FAM.INumberValue)?.value}</span>;
                            change = change || otherField?.value?.value !== (field?.value as FAM.INumberValue)?.value
                        }
                    }
                }
            });
        }

        if (mergeFS && !mergeFS.deactivated) {

            if (isGroupNumber) {
                _itemsMerge["cesNumber"] = (mergeFS as FAM.IGroupNumber)?.cesNumberRef?.name;
                _itemsMerge["groupNumber"] = `${(mergeFS as FAM.IGroupNumber)?.groupNumber} ${(mergeFS as FAM.IGroupNumber)?.status?.value === EBenefitStatus.Cancelled ? '(Cancelled)' : ''}`;
                _itemsMerge["planRef"] = (mergeFS as FAM.IGroupNumber)?.planRef?.name;
                _itemsMerge["stopLossRef"] = (mergeFS as FAM.IGroupNumber)?.stopLossRef?.name;
                _itemsMerge["accountsReceivableRef"] =  (mergeFS as FAM.IGroupNumber)?.accountsReceivableRef?.name;
                _itemsMerge["status"] = (mergeFS as FAM.IGroupNumber)?.status?.name;

            }
            if (isNonStandard) {
                _itemsMerge["name"] = (mergeFS as FAM.INonStandard)?.name;
                _itemsMerge["referenceOnly"] = (mergeFS as FAM.INonStandard)?.referenceOnly ? 'Yes' : 'No';
            }
            mergeFS.fields.forEach(eachField => {
                for (var i = 0; i < eachField.values.length; i++) {
                    const field = eachField.values[i];
                    const fieldName = `${eachField.configRef.id}-${eachField.values[i]?.tierRef?.id ?? ''}`

                    if (field.value?.type === "string") {
                        _itemsMerge[fieldName] = field.value.value
                    } else if (field.value?.type === "bool") {
                        _itemsMerge[fieldName] = field.value.value
                    } else if (field.value?.type === "date") {
                        _itemsMerge[fieldName] = field.value.value
                    } else if (field.value?.type === "optionset") {
                        _itemsMerge[fieldName] = field.value.name
                    } else if (field.value?.type === "entity") {
                        _itemsMerge[fieldName] = field.value.name
                    } else if (field.value?.type === "number") {
                        _itemsMerge[fieldName] = field.value.value
                    }
                }
            });
        }
        if (compareFS) {
            if (props.state.status.displayAll || change) {
                _itemsInCommon.push(_items);
                _itemsInCommon2.push(_items2);
                _itemsInCommonMerge.push(_itemsMerge);
            }
        } else {
            _itemsLeft.push(_items);
            _itemsRight.push(blank);
            _itemsInMerge.push(_itemsMerge);
        }
    });
    if (_itemsInCommon.length > 0) {
        items = items.concat(_itemsInCommon);
        items2 = items2.concat(_itemsInCommon2);
        itemsMerge = itemsMerge.concat(_itemsInCommonMerge);
    }
    if (_itemsLeft.length > 0) {
        items = items.concat(_itemsLeft);
        items2 = items2.concat(_itemsRight);
        itemsMerge = itemsMerge.concat(_itemsInMerge);
    }
    const startIndex: number = items.length;
    flexibleFieldSets2.forEach((eFieldSet, ix1) => {
        const mergeFS: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard | undefined = (flexibleFieldSetsMerge as any).find((n: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard) => n.id === eFieldSet.id);
        const keyID: string = `${isGroupNumber ? (eFieldSet as FAM.IGroupNumber).groupNumber : eFieldSet.id}` ?? eFieldSet.id;

        if (!(target as any).find((n: FAM.IFlexibleFieldSet | FAM.IGroupNumber | FAM.INonStandard) => {
            if (isGroupNumber) {
                return (n as FAM.IGroupNumber).groupNumber === (eFieldSet as FAM.IGroupNumber).groupNumber
            } else {
                return n.id === eFieldSet.id
            }
        })) {

            const _items: TFlexibleFieldGridItems = {
                _index: ix1 + startIndex,
                key: keyID,
                data: eFieldSet
            };
            const _itemsMerge: TFlexibleFieldGridItems = {
                _index: ix1 + startIndex,
                key: keyID,
                data: eFieldSet
            };
            if (isGroupNumber) {

                _items["cesNumber"] =
                    <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.cesNumberRef?.name}</span>;
                _items["groupNumber"] = <span
                    style={highlightStyles}>{`${(eFieldSet as FAM.IGroupNumber)?.groupNumber} ${(eFieldSet as FAM.IGroupNumber)?.status?.value === EBenefitStatus.Cancelled ? '(Cancelled)' : ''}`}</span>;
                _items["planRef"] = <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.planRef?.name}</span>;
                _items["stopLossRef"] =  <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.stopLossRef?.name}</span>;
                _items["accountsReceivableRef"] =  <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.accountsReceivableRef?.name}</span>;
                _items["status"] = <span style={highlightStyles}>{(eFieldSet as FAM.IGroupNumber)?.status?.name}</span>;

            }
            if (isNonStandard) {
                _items["name"] = <span style={highlightStyles}>{(eFieldSet as FAM.INonStandard)?.name}</span>;
                _items["referenceOnly"] =
                    <span style={highlightStyles}>{(eFieldSet as FAM.INonStandard)?.referenceOnly ? 'Yes' : 'No'}</span>;
            }
            _itemsMerge['merge'] =
                <Toggle onText="Right" offText="Left"
                        key={keyID}
                        onChange={(event, checked) => {
                            setCounter(counter + 1);
                            setLeftRight((prevState) => {
                                if (prevState[eFieldSet.id] !== undefined && prevState[eFieldSet.id] !== null)
                                    return {
                                        ...prevState,
                                        [eFieldSet.id]: !prevState[eFieldSet.id]
                                    }
                                return {...prevState, [eFieldSet.id]: !props.leftright}
                            })

                            // For each category attribute set, or nonstandard, we need to appropriately either:
                            // 1) Slice the existing array and replace an item in the array
                            // 2) Slice the existing array to remove an item
                            // 3) Add an item to the existing array

                            // This is the toggle for the second snapshot item where a match was not found with the first snapshot.
                            // Therefore, case #2 for slicing to remove an item should not happen, but we will code for it anyway to prevent duplication
                            // in case there is a reason why this might happen

                            if (category.bcbs_config_categoryid === categoryMapByName['Group Configuration'].bcbs_config_categoryid) {
                                const fieldIndex = props.state.comp.mergeSnap.json?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                                if (checked) {
                                    //Toggle Right
                                    if (fieldIndex > -1)
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                attributeSets: [...props.state.comp.mergeSnap.json?.attributeSets?.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.attributeSets?.slice(fieldIndex + 1)]
                                            }
                                        }));
                                    else
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                attributeSets: [...props.state.comp.mergeSnap.json?.attributeSets, eFieldSet]
                                            }
                                        }));

                                } else {
                                    //Toggle Left
                                    if (fieldIndex !== -1) {
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                attributeSets: [...props.state.comp.mergeSnap.json?.attributeSets?.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.attributeSets?.slice(fieldIndex + 1)]
                                            }
                                        }));
                                    }
                                }
                            } else if (isNonStandard) {
                                if (props.level === "groupConfiguration") {
                                    const fieldIndex = props.state.comp.mergeSnap.json?.nonStandards?.findIndex(q => q.id === eFieldSet.id) ?? -1;
                                    if (checked) {
                                        if (fieldIndex > -1)
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards?.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.nonStandards?.slice(fieldIndex + 1)]
                                                }
                                            }));
                                        else
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards, eFieldSet]
                                                }
                                            }));
                                    } else {
                                        if (fieldIndex !== -1) {
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    nonStandards: [...props.state.comp.mergeSnap.json?.nonStandards?.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.nonStandards?.slice(fieldIndex + 1)]
                                                }
                                            }));
                                        }
                                    }
                                } else if (props.level === "section") {
                                    const benefit = props.state.comp.mergeSnap.json?.benefits?.find(p => p.id === props.currentBenefit.id);
                                    const benefitIndex = props.state.comp.mergeSnap.json?.benefits?.findIndex(p => p.id === props.currentBenefit.id);
                                    const fieldIndex = benefit?.nonStandards?.findIndex(n => n.id === eFieldSet.id) ?? -1;
                                    // @ts-ignore
                                    let newBenefit = {...benefit!};
                                    if (checked) {
                                        if (fieldIndex > -1)
                                            newBenefit.nonStandards = [...benefit!.nonStandards.slice(0, fieldIndex), (eFieldSet as INonStandard), ...benefit!.nonStandards.slice(fieldIndex + 1)];
                                        else
                                            newBenefit.nonStandards = [...benefit!.nonStandards, (eFieldSet as INonStandard)];
                                    } else {
                                        if (fieldIndex !== -1) {
                                            newBenefit.nonStandards = [...benefit!.nonStandards.slice(0, fieldIndex), ...benefit!.nonStandards.slice(fieldIndex + 1)];
                                        }
                                    }
                                    dispatch(changeMergeSnap({
                                        ...props.state.comp.mergeSnap, json: {
                                            ...props.state.comp.mergeSnap.json,
                                            benefits: [...props.state.comp.mergeSnap.json?.benefits?.slice(0, benefitIndex), newBenefit, ...props.state.comp.mergeSnap.json?.benefits?.slice(benefitIndex + 1)]
                                        }
                                    }));
                                }
                            } else if (category === categoryMapByName["Group Number"].bcbs_config_categoryid) {
                                const fieldIndex = props.state.comp.mergeSnap.json?.groupStructure.groupNumbers.findIndex(q => q.groupNumber === (eFieldSet as FAM.IGroupNumber).groupNumber) ?? -1;
                                if (checked) {
                                    const cesNumber = props.state.comp.rightSnap.json?.groupStructure.cesNumbers.find(q => q.cesNumber === (eFieldSet as FAM.IGroupNumber).cesNumberRef?.name)
                                    const cesIndex = props.state.comp.mergeSnap.json?.groupStructure.cesNumbers.findIndex(q => q.cesNumber === cesNumber?.cesNumber);
                                    if (cesNumber) {
                                        if (fieldIndex > -1) {
                                            if (cesIndex > -1)
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(0, cesIndex), cesNumber, ...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(cesIndex + 1)]
                                                        }
                                                    }
                                                }));
                                            else
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers, cesNumber]
                                                        }
                                                    }
                                                }));
                                        } else {
                                            if (cesIndex > -1)
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(0, cesIndex), cesNumber, ...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers?.slice(cesIndex + 1)]
                                                        }
                                                    }
                                                }));
                                            else
                                                dispatch(changeMergeSnap({
                                                    ...props.state.comp.mergeSnap, json: {
                                                        ...props.state.comp.mergeSnap.json,
                                                        groupStructure: {
                                                            ...props.state.comp.mergeSnap.json.groupStructure,
                                                            groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                                            cesNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.cesNumbers, cesNumber]
                                                        }
                                                    }
                                                }));
                                        }
                                    } else {
                                        if (fieldIndex > -1)
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    groupStructure: {
                                                        ...props.state.comp.mergeSnap.json.groupStructure,
                                                        groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), eFieldSet, ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)],
                                                    }
                                                }
                                            }));
                                        else
                                            dispatch(changeMergeSnap({
                                                ...props.state.comp.mergeSnap, json: {
                                                    ...props.state.comp.mergeSnap.json,
                                                    groupStructure: {
                                                        ...props.state.comp.mergeSnap.json.groupStructure,
                                                        groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers, eFieldSet],
                                                    }
                                                }
                                            }));
                                    }
                                } else {
                                    dispatch(changeMergeSnap({
                                        ...props.state.comp.mergeSnap, json: {
                                            ...props.state.comp.mergeSnap.json,
                                            groupStructure: {
                                                ...props.state.comp.mergeSnap.json?.groupStructure,
                                                groupNumbers: [...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.groupStructure?.groupNumbers?.slice(fieldIndex + 1)]
                                            }
                                        }
                                    }));

                                }

                            } else if (category.bcbs_config_categoryid === categoryMapByName['Plan'].bcbs_config_categoryid) {
                                const indexer = props.state.comp.mergeSnap.json?.plans?.findIndex(q => q.id === scope?.id);
                                const fieldIndex = props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                                let newItem = props.state.comp.mergeSnap.json?.plans?.[indexer];
                                if (checked) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.plans[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.plans[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.plans?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                        };
                                    dispatch(changeMergeSnap({
                                        ...props.state.comp.mergeSnap, json: {
                                            ...props.state.comp.mergeSnap.json,
                                            plans: [...props.state.comp.mergeSnap.json?.plans?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.plans?.slice(indexer + 1)]
                                        }
                                    }));

                                } else {
                                    if (fieldIndex !== -1) {
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                plans: [...props.state.comp.mergeSnap.json?.plans?.slice(0, indexer), ...props.state.comp.mergeSnap.json?.plans?.slice(indexer + 1)]
                                            }
                                        }));
                                    }
                                }
                            } else if (category.bcbs_config_categoryid === categoryMapByName["Fees and Premiums"].bcbs_config_categoryid) {
                                // Fees And Premiums
                                const indexer = props.state.comp.mergeSnap.json?.feesAndPremiums?.findIndex(q => q.id === scope?.id);
                                const fieldIndex = props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                                let newItem = props.state.comp.mergeSnap.json?.feesAndPremiums[indexer];
                                if (checked) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.feesAndPremiums[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.feesAndPremiums[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                        };
                                    dispatch(changeMergeSnap({
                                        ...props.state.comp.mergeSnap, json: {
                                            ...props.state.comp.mergeSnap.json,
                                            feesAndPremiums: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.feesAndPremiums?.slice(indexer + 1)]
                                        }
                                    }));

                                } else {
                                    if (fieldIndex !== -1) {
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                feesAndPremiums: [...props.state.comp.mergeSnap.json?.feesAndPremiums?.slice(0, indexer), ...props.state.comp.mergeSnap.json?.feesAndPremiums?.slice(indexer + 1)]
                                            }
                                        }));
                                    }
                                }
                            } else if (category.bcbs_config_categoryid === categoryMapByName["Stop Loss"].bcbs_config_categoryid) {
                                // stop loss
                                const indexer = props.state.comp.mergeSnap.json?.stopLoss?.findIndex(q => q.id === scope?.id);
                                const fieldIndex = props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                                let newItem = props.state.comp.mergeSnap.json?.stopLoss[indexer];
                                if (checked) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.stopLoss[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.stopLoss[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.stopLoss?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                        };
                                    dispatch(changeMergeSnap({
                                        ...props.state.comp.mergeSnap, json: {
                                            ...props.state.comp.mergeSnap.json,
                                            stopLoss: [...props.state.comp.mergeSnap.json?.stopLoss?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.stopLoss?.slice(indexer + 1)]
                                        }
                                    }));

                                } else {
                                    if (fieldIndex !== -1) {
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                stopLoss: [...props.state.comp.mergeSnap.json?.stopLoss?.slice(0, indexer), ...props.state.comp.mergeSnap.json?.stopLoss?.slice(indexer + 1)]
                                            }
                                        }));
                                    }
                                }
                            } else if (category.bcbs_config_categoryid === categoryMapByName["Accounts Receivable"].bcbs_config_categoryid) {
                                //claims AR
                                const indexer = props.state.comp.mergeSnap.json?.claimsARs?.findIndex(q => q.id === scope?.id);
                                const fieldIndex = props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                                let newItem = props.state.comp.mergeSnap.json?.claimsARs[indexer];
                                if (checked) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.claimsARs[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.claimsARs[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.claimsARs?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                        };
                                    dispatch(changeMergeSnap({
                                        ...props.state.comp.mergeSnap, json: {
                                            ...props.state.comp.mergeSnap.json,
                                            claimsARs: [...props.state.comp.mergeSnap.json?.claimsARs?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.claimsARs?.slice(indexer + 1)]
                                        }
                                    }));

                                } else {
                                    if (fieldIndex !== -1) {
                                        dispatch(changeMergeSnap({
                                            ...props.state.comp.mergeSnap, json: {
                                                ...props.state.comp.mergeSnap.json,
                                                claimsARs: [...props.state.comp.mergeSnap.json?.claimsARs?.slice(0, indexer), ...props.state.comp.mergeSnap.json?.claimsARs?.slice(indexer + 1)]
                                            }
                                        }));
                                    }
                                }
                            } else if (props.state.data.config.cache.bcbs_config_category_benefits.some(q => q.bcbs_config_categoryid === category.bcbs_config_categoryid)) {
                                const indexer = props.state.comp.mergeSnap.json?.benefits?.findIndex(q => q.id === scope?.id);
                                const fieldIndex = props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets?.findIndex(q => q.attributeSetRef.id === props.masterAttribute?.bcbs_config_masterattributeid && q.id === eFieldSet.id) ?? -1;
                                let newItem = props.state.comp.mergeSnap.json?.benefits[indexer];
                                if (checked) {
                                    if (fieldIndex > -1)
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(0, fieldIndex), (eFieldSet as IFlexibleFieldSet), ...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };
                                    else
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets, (eFieldSet as IFlexibleFieldSet)]
                                        };

                                } else {
                                    if (fieldIndex !== -1) {
                                        newItem = {
                                            ...props.state.comp.mergeSnap.json?.benefits[indexer],
                                            attributeSets: [...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(0, fieldIndex), ...props.state.comp.mergeSnap.json?.benefits?.[indexer]?.attributeSets!.slice(fieldIndex + 1)]
                                        };

                                    }
                                }
                                dispatch(changeMergeSnap({
                                    ...props.state.comp.mergeSnap, json: {
                                        ...props.state.comp.mergeSnap.json,
                                        benefits: [...props.state.comp.mergeSnap.json?.benefits?.slice(0, indexer), newItem, ...props.state.comp.mergeSnap.json?.benefits?.slice(indexer + 1)]
                                    }
                                }));
                            }
                        }}
                        checked={leftRight[eFieldSet.id] !== undefined ? leftRight[eFieldSet.id]! : props.leftright}
                        style={{}} onClick={(event) => {
                    if (event?.stopPropagation) event.stopPropagation();
                }}

                />;
            eFieldSet.fields.forEach(eachField => {

                for (var i = 0; i < eachField.values.length; i++) {
                    const field = eachField.values[i];
                    const fieldName = `${eachField.configRef.id}-${eachField.values[i]?.tierRef?.id ?? ''}`

                    if (field.value?.type === "string") {
                        _items[fieldName] = <span style={highlightStyles}>{field.value.value}</span>;
                    } else if (field.value?.type === "bool") {
                        _items[fieldName] = <span style={highlightStyles}>{field.value.value ? "Yes" : "No"}</span>;
                    } else if (field.value?.type === "date") {
                        _items[fieldName] = <span
                            style={highlightStyles}>{formatDate(parseDate((field.value as IDateValue).value, 'local:yyyy-MM-dd'), 'en-US-ET')}</span>;
                    } else if (field.value?.type === "optionset") {
                        _items[fieldName] = <span style={highlightStyles}>{field.value.name}</span>;
                    } else if (field.value?.type === "entity") {
                        _items[fieldName] = <span style={highlightStyles}>{field.value.name}</span>;
                    } else if (field.value?.type === "number") {
                        _items[fieldName] = <span style={highlightStyles}>{field.value.value}</span>;
                    }
                }
            });
            if (mergeFS) {
                if (isGroupNumber) {
                    _itemsMerge["cesNumber"] = (mergeFS as FAM.IGroupNumber)?.cesNumberRef?.name;
                    _itemsMerge["groupNumber"] = `${(mergeFS as FAM.IGroupNumber)?.groupNumber} ${(mergeFS as FAM.IGroupNumber)?.status?.value === EBenefitStatus.Cancelled ? '(Cancelled)' : ''}`;
                    _itemsMerge["planRef"] = (mergeFS as FAM.IGroupNumber)?.planRef?.name;
                    _itemsMerge["stopLossRef"] = (mergeFS as FAM.IGroupNumber)?.stopLossRef?.name;
                    _itemsMerge["accountsReceivableRef"] =  (mergeFS as FAM.IGroupNumber)?.accountsReceivableRef?.name;
                    _itemsMerge["status"] = (mergeFS as FAM.IGroupNumber)?.status?.name;

                }
                if (isNonStandard) {
                    _itemsMerge["name"] = (mergeFS as FAM.INonStandard)?.name;
                    _itemsMerge["referenceOnly"] = (mergeFS as FAM.INonStandard)?.referenceOnly ? 'Yes' : 'No';
                }
                mergeFS.fields.forEach(eachField => {

                    for (var i = 0; i < eachField.values.length; i++) {
                        const field = eachField.values[i];
                        const fieldName = `${eachField.configRef.id}-${eachField.values[i]?.tierRef?.id ?? ''}`

                        if (field.value?.type === "string") {
                            _itemsMerge[fieldName] = field.value.value
                        } else if (field.value?.type === "bool") {
                            _itemsMerge[fieldName] = field.value.value
                        } else if (field.value?.type === "date") {
                            _itemsMerge[fieldName] = field.value.value
                        } else if (field.value?.type === "optionset") {
                            _itemsMerge[fieldName] = field.value.name
                        } else if (field.value?.type === "entity") {
                            _itemsMerge[fieldName] = field.value.name
                        } else if (field.value?.type === "number") {
                            _itemsMerge[fieldName] = field.value.value
                        }
                    }
                });
            }
            const blank: TFlexibleFieldGridItems = {
                key: "blank" + ix1 + startIndex,
                _index: ix1 + startIndex,
            }
            items2.push(_items);
            items.push(blank);
            itemsMerge.push(_itemsMerge);
        }
    });


    const width = isGroupNumber ? props.merge ? '30%' : '48%' : '100%';

    const costshareCategory = undefined;

    if (items.length > 0){
        return(<div>
        {isNonStandard &&       <NonStandardEditor
                isOpen={nonStandard !== null}
                onDismiss={() => { setNonStandard(null); }}
                defaultValue={nonStandard}
                costshareCategory={costshareCategory}
                benefit={props.currentBenefit}
                disabled={true}
            />}
    <CustomPanel title={props.label}
                 canCollapse={true}
                 style={{marginTop: '15px'}}
                 defaultCollapsed={false}
    >
        <Stack horizontal disableShrink horizontalAlign="space-evenly" style={{maxWidth: '100%'}}>
            {((props.snap && props.side === 'left') || isGroupNumber) &&
                <Stack.Item grow style={{maxWidth: width}}>
                    <StickyDetailsList
                        items={items}
                        columns={columns}
                        compact={true}
                        selectionMode={SelectionMode.none}
                        onItemInvoked={(item: {data: FAM.INonStandard}, index, event) => {
                            if (item.data && isNonStandard)
                                setNonStandard(item.data);
                        }}
                    />
                </Stack.Item>
            }
            {((props.snap2 && props.side === 'right') || isGroupNumber) &&
                <Stack.Item grow style={{maxWidth: width}}>
                    <StickyDetailsList
                        items={items2}
                        columns={columns}
                        compact={true}
                        selectionMode={SelectionMode.none}
                        onItemInvoked={(item: {data: FAM.INonStandard}, index, event) => {
                        if (item.data && isNonStandard)
                            setNonStandard(item.data);
                    }}
                    />
                </Stack.Item>
            }
            {(props.side === 'merge' || (isGroupNumber && props.merge)) &&
                <Stack.Item grow style={{maxWidth: width}}
                            className={`${counter}merge`}>
                    <StickyDetailsList
                        styles={gridStyles}
                        items={itemsMerge}
                        columns={[{
                            key: 'mergetoggle',
                            name: 'Merge',
                            minWidth: 100,
                            fieldName: 'merge',
                            maxWidth: 150,
                            isResizable: false
                        },

                            ...columns]}

                        compact={true}
                        selectionMode={SelectionMode.none}
                        onItemInvoked={(item: {data: FAM.INonStandard}, index, event) => {
                            if (item.data && isNonStandard)
                                setNonStandard(item.data);
                        }}
                    />
                </Stack.Item>
            }
        </Stack>

    </CustomPanel></div>);
}
    else {
        if(props.noDiff)
        props.noDiff();
        return null;
    }
}
AttributeSetGridView.displayName = 'AttributeSetGridView';
function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(AttributeSetGridView)
